import { Component, Inject, inject, Optional } from '@angular/core';
import {
    AbstractStepDTO,
    ConfiguratorI18nCacheService,
    ConfiguratorLandingPageService,
    ConfiguratorStatisticsCacheService,
    ConfiguratorTransactionalEmailService,
    EmailLandingPageLink,
    EmailStepDTO,
    LandingPageDTO
} from '@tj/tj-dmp-lib-api';
import { MessageService } from 'primeng/api';
import { ClipboardService } from '../../../services/clipboard.service';
import { landingPageLinkParamName, landingPageOptions } from '../../../shared/landing-page-link.util';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {AbstractStepConfigComponent} from '../abstract-step-config/abstract-step-config.component';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
    ON_CANCEL_TOKEN,
    ON_SAVE_TOKEN,
    PARTNER_TOKEN,
    READ_ONLY_MODE_TOKEN,
    STEP_TOKEN,
    TENANT_TOKEN
} from '../injection-tokens';
import {TranslateService} from '@ngx-translate/core';
import { StepDescriptionService } from '../step-description.service';

@Component({
    selector: 'dmp-email-step-config',
    templateUrl: './email-step-config.component.html',
    standalone: false
})
export class EmailStepConfigComponent extends AbstractStepConfigComponent {
  private readonly stepDescriptionService = inject(StepDescriptionService);

    emailForm: FormGroup;
  availableEmails: { label: string; value: string}[] = [];
  availableLanguages: {label: string; value: string}[]=[];
  availableAffiliates: { label: string; value: string }[] = [];
  availableLandingPages: { label: string; value: string }[] = [];
  private landingPagesByCode = new Map<string, LandingPageDTO>();
  availableTargetTables: { label: string; value: string }[] = [];

  // Email statistics
  emailStatistics = {
    emailsSent: 0,
    emailsReceived: 0,
    emailsOpened: 0,
    hardbounces: 0,
    softbounces: 0
  };

  get emailStep(): EmailStepDTO {
    return this.step as EmailStepDTO;
  }

  get emailTemplateId() { return this.emailForm.get('emailTemplateId'); }
  get selectedLanguage() { return this.emailForm.get('selectedLanguage'); }
  get selectedAffiliate() { return this.emailForm.get('selectedAffiliate'); }
  get landingPageLinksArray() { return this.emailForm.get('landingPageLinks') as FormArray; }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: string,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private emailService: ConfiguratorTransactionalEmailService,
    private formBuilder: FormBuilder,
    private i18nCacheService: ConfiguratorI18nCacheService,
    private statisticsCacheService: ConfiguratorStatisticsCacheService,
    private translateService: TranslateService,
    private landingPageService: ConfiguratorLandingPageService,
    private clipboardService: ClipboardService,
    @Optional() private messageService: MessageService
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.setupEmailChangeSubscription();
    this.loadEmails();
    this.loadAffiliates();
    this.loadLandingPages();
    if (this.readOnlyMode) {
      this.loadEmailStatistics();
    }
  }

  protected updateStepFromForm() {
    if (!this.emailStep || !this.emailForm.valid) return;

    const { emailTemplateId, selectedLanguage, selectedAffiliate } = this.emailForm.value;

    this.emailStep.description = this.generateReadableDescription(emailTemplateId);

    this.emailStep.emailTemplateId = emailTemplateId;

    if ([EmailStepDTO.LanguageModeEnum.FROMCONTACT, EmailStepDTO.LanguageModeEnum.DEFAULT].includes(selectedLanguage)) {
      this.emailStep.languageMode = selectedLanguage;
      this.emailStep.fixedLanguage = undefined;
    } else {
      this.emailStep.languageMode = EmailStepDTO.LanguageModeEnum.FIXED;
      this.emailStep.fixedLanguage = selectedLanguage;
    }

    if (selectedAffiliate === EmailStepDTO.AffiliateModeEnum.FROMDATA) {
      this.emailStep.affiliateMode = EmailStepDTO.AffiliateModeEnum.FROMDATA;
      this.emailStep.defaultAffiliateCode = undefined;
    } else {
      this.emailStep.affiliateMode = EmailStepDTO.AffiliateModeEnum.FIXED;
      this.emailStep.defaultAffiliateCode = selectedAffiliate;
    }

    this.emailStep.landingPageLinks = (this.landingPageLinksArray.value as EmailLandingPageLink[])
      .map(link => ({ landingPageCode: link.landingPageCode, targetTable: link.targetTable }));
  }

  addLandingPageLink(): void {
    if (this.readOnlyMode) return;
    this.landingPageLinksArray.push(this.createLandingPageLinkGroup());
  }

  /** Variable à utiliser dans le template Brevo pour le lien de la landing page choisie. */
  linkVariable(landingPageCode: string): string {
    const name = landingPageLinkParamName(landingPageCode);
    return name ? `{{ params.data.${name} }}` : '';
  }

  copyLinkVariable(landingPageCode: string): void {
    const variable = this.linkVariable(landingPageCode);
    this.clipboardService.copyToClipboard(variable).then(() => this.messageService?.add({
      severity: 'success',
      summary: variable,
      detail: this.translateService.instant('components.workflowStepConfig.emailStepConfig.landingPageLinks.copied')
    }));
  }

  /** Durée du lien et email envoyé à son expiration, d'après la configuration de la landing page. */
  linkExpiryNote(landingPageCode: string): string {
    const landingPage = this.landingPagesByCode.get(landingPageCode);
    if (!landingPage?.withToken || !landingPage.periodValidityMin) {
      return '';
    }
    const key = landingPage.mailTemplateRenewalToken ? 'expiryWithRenewal' : 'expiryWithoutRenewal';
    return this.translateService.instant('components.workflowStepConfig.emailStepConfig.landingPageLinks.' + key, {
      minutes: landingPage.periodValidityMin,
      template: landingPage.mailTemplateRenewalToken
    });
  }

  removeLandingPageLink(index: number): void {
    if (this.readOnlyMode) return;
    this.landingPageLinksArray.removeAt(index);
  }

  protected isConfigurationValid(): boolean {
    return this.emailForm.valid;
  }

  private createForm(): void {
    this.emailForm = this.formBuilder.group({
      emailTemplateId: this.createFormControl('', [Validators.required]),
      selectedLanguage: this.createFormControl('', [Validators.required]),
      selectedAffiliate: this.createFormControl('', [Validators.required]),
      landingPageLinks: this.formBuilder.array([]),
    });

    this.emailForm.valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      if (!this.isInitializing) {
        this.markAsChanged()
      }
    });
  }

  private setupEmailChangeSubscription(): void {
    this.emailForm.get('emailTemplateId')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      filter(emailId => !!emailId),
      distinctUntilChanged()
    ).subscribe(emailId => {
      this.emailForm.patchValue({
        selectedLanguage: ''
      });
      this.loadLanguagesForEmail(emailId);
    });
  }

  private loadLanguagesForEmail(emailId: string): void {
    // TODO: Remplacer par l'appel API réel
    // Pour l'instant, langues fixes
    this.availableLanguages = [{
      label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.languages.fromContact'),
      value: EmailStepDTO.LanguageModeEnum.FROMCONTACT
    }, {
        label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.languages.default'),
      value: EmailStepDTO.LanguageModeEnum.DEFAULT
    }, {
        label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.languages.french'),
      value: 'FR'
    }, {
      label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.languages.english'),
      value: 'EN'
    }, {
        label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.languages.italian'),
      value: 'IT'
    }];
  }

  private createLandingPageLinkGroup(link?: EmailLandingPageLink): FormGroup {
    return this.formBuilder.group({
      landingPageCode: this.createFormControl(link?.landingPageCode || '', [Validators.required]),
      targetTable: this.createFormControl(link?.targetTable || 'personal', [Validators.required]),
    });
  }

  private loadLandingPages(): void {
    this.availableTargetTables = ['command', 'personal'].map(table => ({
      label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.landingPageLinks.targetTables.' + table),
      value: table
    }));
    this.landingPageService.getAllLandingPagesUsingGET(this.tenant, 0, 100).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: landingPages => {
        this.landingPagesByCode = new Map(landingPages.filter(landingPage => !!landingPage.code)
          .map(landingPage => [landingPage.code, landingPage]));
        this.availableLandingPages = landingPageOptions(landingPages);
      },
      error: error => console.error('Erreur chargement landing pages:', error)
    });
  }

  private loadAffiliates(): void {
    this.i18nCacheService.searchClientUsingGET().subscribe(
      res => {
        this.availableAffiliates = [{
          label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.affiliates.fromData'),
          value: EmailStepDTO.AffiliateModeEnum.FROMDATA
        }, {
          label: this.translateService.instant('components.workflowStepConfig.emailStepConfig.affiliates.default'),
          value: EmailStepDTO.AffiliateModeEnum.DEFAULT
        },
        ...res.map(r => ({ label: r.value, value: r.key }))
        ]
      }
    )
  }

  private loadEmails(): void {
    if (!this.tenant) {
      console.error('Erreur chargement emails: tenant is undefined');
      return;
    }

      this.emailService.getAllTransactionalEmailsUsingGET1(this.tenant, 0, 100, [], undefined, 'response').pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: emails => {
          this.availableEmails = emails.body
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(email => ({ label: email.name, value: email.id }));

          this.initializeForm();
      },
      error: error => {
        console.error('Erreur chargement emails:', error);
        console.error('Tenant value:', this.tenant);
      }
    });
  }

  private initializeForm(): void {
    if (!this.emailStep.emailTemplateId) return;

    this.isInitializing = true;

    const selectedLanguage = [EmailStepDTO.LanguageModeEnum.FROMCONTACT, EmailStepDTO.LanguageModeEnum.DEFAULT].includes(this.emailStep.languageMode)
      ? this.emailStep.languageMode
      : this.emailStep.fixedLanguage;

    const selectedAffiliate = this.emailStep.affiliateMode === EmailStepDTO.AffiliateModeEnum.FROMDATA
      ? EmailStepDTO.AffiliateModeEnum.FROMDATA
      : this.emailStep.defaultAffiliateCode;

    (this.emailStep.landingPageLinks || []).forEach(link =>
      this.landingPageLinksArray.push(this.createLandingPageLinkGroup(link))
    );

    this.emailForm.patchValue({
      emailTemplateId: this.emailStep.emailTemplateId,
      selectedLanguage: selectedLanguage || '',
      selectedAffiliate: selectedAffiliate || ''
    });

    this.isInitializing = false;
  }

  private generateReadableDescription(templateId: string): string {
    const template = this.availableEmails.find(email => email.value === templateId);
    return this.stepDescriptionService.email(template?.label || templateId);
  }

  private loadEmailStatistics(): void {
    if (!this.tenant || !this.emailStep.stepPath) {
      console.warn('Cannot load email statistics: missing tenant or stepPath');
      return;
    }

    // Build a category identifier for this specific email step
    const category = `email-step-${this.emailStep.stepPath}`;

    this.statisticsCacheService.getStatisticsCacheUsingGET(this.tenant, category).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (statisticsCache) => {
        if (statisticsCache && statisticsCache.dataByPartner) {
          const partnerData = statisticsCache.dataByPartner[this.partner] || statisticsCache.dataByPartner;

          this.emailStatistics = {
            emailsSent: partnerData.emailsSent || 0,
            emailsReceived: partnerData.emailsReceived || 0,
            emailsOpened: partnerData.emailsOpened || 0,
            hardbounces: partnerData.hardbounces || 0,
            softbounces: partnerData.softbounces || 0
          };
        }
      },
      error: (error) => {
        console.warn('Could not load email statistics:', error);
        // Keep default values of 0
      }
    });
  }

}
